import type { AuthActionResponse } from '@refinedev/core';

export const LOGIN_FAILED_TITLE = 'Couldn’t sign in';
export const LOGIN_NO_ANSWER_TEXT = 'No answer from the server. Wait a moment, then sign in again.';
export const LOGIN_NO_NETWORK_TEXT = 'No network. Sign in again when the signal is back.';
const LOGIN_FALLBACK_TEXT = 'Check your email and password, then try again.';

type ErrorLike = { message?: unknown; code?: unknown; statusCode?: unknown; response?: unknown };

/** The sign-in service did not answer: the gateway could not reach it (502–504), or no answer came at all. */
const noAnswer = (e: ErrorLike) =>
  [502, 503, 504].includes(Number(e.statusCode)) ||
  (!e.response &&
    /network error|timeout|ECONNABORTED|ERR_NETWORK|Failed to fetch/i.test(
      `${String(e.message ?? '')} ${String(e.code ?? '')}`,
    ));

/** A developer's word (an error class or a library's text) is never shown to a worker. */
const plain = (text: unknown) =>
  typeof text === 'string' &&
  text.trim() !== '' &&
  !/AxiosError|Request failed|Network Error|Login Error|status code/i.test(text)
    ? text
    : undefined;

/**
 * Hub's sign-in failure in plain words: the toast's title says it did not sign in, the line under it says why and
 * what to do (round 30: during a sign-in service restart the toast showed "AxiosError" and "Nothing saved").
 */
export const hubLoginFailure = (error: unknown): AuthActionResponse => {
  const e = (error && typeof error === 'object' ? error : {}) as ErrorLike;
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  return {
    success: false,
    error: {
      name: LOGIN_FAILED_TITLE,
      message: offline
        ? LOGIN_NO_NETWORK_TEXT
        : noAnswer(e)
          ? LOGIN_NO_ANSWER_TEXT
          : (plain(e.message) ?? LOGIN_FALLBACK_TEXT),
    },
  };
};

/** A refused sign-in (wrong password): the server's reason, under the same plain title. */
export const hubLoginRefused = (result: AuthActionResponse): AuthActionResponse =>
  result.success
    ? result
    : {
        ...result,
        error: {
          name: LOGIN_FAILED_TITLE,
          message: plain(result.error?.name) ?? plain(result.error?.message) ?? LOGIN_FALLBACK_TEXT,
        },
      };
